import React, { useEffect, useState } from 'react';
import { Modal, Button, Typography, Slider, theme, ConfigProvider } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import TaxonomyPicker from './TaxonomyPicker';
import { IOperatorSearchFilters } from '../interfaces/heavyDutyMachinery/IOperator';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { filterSliderTheme } from '../constants/filterSliderTheme';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';

const { Text } = Typography;

const HOURLY_RATE_RANGE: [number, number] = [0, 200];

interface OperatorFiltersModalProps {
  open: boolean;
  onClose: () => void;
  filters: IOperatorSearchFilters;
  totalCount: number;
  onChange: (patch: Partial<IOperatorSearchFilters>) => void;
  onClear: () => void;
}

/**
 * Operator tab's filters overlay on /explore's mobile map -- same theme,
 * layout, and mobile-fullscreen/desktop-Modal fork as FiltersModal
 * (Machinery's own), just a narrower field set: QualifiedCategories tile
 * grid (same Machinery.Category vocabulary/icons Machinery's own Category
 * filter uses) and an HourlyRate range slider. No verified toggle -- see
 * OperatorController.SearchOperators' own doc comment, only Verified+
 * Active operators are ever returned, there's nothing to toggle. No facet
 * counts either -- unlike Machinery's own search, this endpoint builds no
 * facets, so tiles render without the small count badge Machinery's own
 * show.
 */
const OperatorFiltersModal: React.FC<OperatorFiltersModalProps> = ({ open, onClose, filters, totalCount, onChange, onClear }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();

  const [rateDisplay, setRateDisplay] = useState<[number, number]>([
    filters.minHourlyRate ?? HOURLY_RATE_RANGE[0],
    filters.maxHourlyRate ?? HOURLY_RATE_RANGE[1],
  ]);
  useEffect(() => {
    setRateDisplay([filters.minHourlyRate ?? HOURLY_RATE_RANGE[0], filters.maxHourlyRate ?? HOURLY_RATE_RANGE[1]]);
  }, [filters.minHourlyRate, filters.maxHourlyRate]);

  const renderCategoryGrid = () => (
    <div style={{ marginBottom: 20 }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CATEGORY_LABEL)}
      </Text>
      <TaxonomyPicker
        multiple
        value={filters.category ?? []}
        onChange={(next) => onChange({ category: next as string[] })}
      />
    </div>
  );

  const renderRateSlider = () => (
    <ConfigProvider theme={filterSliderTheme}>
      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_HOURLY_RATE_LABEL)}
        </Text>
        <Slider
          range
          className="heavyDutyMachinery-filter-slider"
          min={HOURLY_RATE_RANGE[0]}
          max={HOURLY_RATE_RANGE[1]}
          value={rateDisplay}
          tooltip={{ formatter: (value) => (value != null ? `${value}€` : '') }}
          onChange={(value) => setRateDisplay(value as [number, number])}
          onChangeComplete={(value) => {
            const [min, max] = value as [number, number];
            onChange({
              minHourlyRate: min === HOURLY_RATE_RANGE[0] ? undefined : min,
              maxHourlyRate: max === HOURLY_RATE_RANGE[1] ? undefined : max,
            });
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Text type="secondary">{rateDisplay[0]}€</Text>
          <Text type="secondary">{rateDisplay[1]}€</Text>
        </div>
      </div>
    </ConfigProvider>
  );

  const renderShowResultsButton = (style?: React.CSSProperties) => (
    <Button type="primary" onClick={onClose} style={{ borderRadius: 999, ...style }}>
      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_SHOW_RESULTS_BUTTON)} ({totalCount})
    </Button>
  );

  const renderClearFiltersButton = () => (
    <button
      type="button"
      onClick={onClear}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 44,
        padding: '0 4px',
        marginLeft: -4,
        textDecoration: 'underline',
        color: token.colorTextSecondary,
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        font: 'inherit',
      }}
    >
      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_CLEAR_FILTERS)}
    </button>
  );

  if (isMobile) {
    return (
      <AnimatePresence>
        {open && (
          <motion.div
            key="operator-filters-overlay"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{ position: 'fixed', inset: 0, zIndex: 2000, background: token.colorBgContainer, display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', flexShrink: 0 }}>
              <Text strong style={{ fontSize: 16 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_TITLE)}
              </Text>
              <button
                type="button"
                aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_OVERLAY_CLOSE)}
                onClick={onClose}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: 'none',
                  background: token.colorFillTertiary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <CloseOutlined style={{ fontSize: 16 }} />
              </button>
            </div>

            <div className="no-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '0 20px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {renderRateSlider()}
              {renderCategoryGrid()}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                padding: '16px 20px',
                paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                flexShrink: 0,
              }}
            >
              {renderClearFiltersButton()}
              {renderShowResultsButton()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(760px, 92vw)"
      transitionName="filters-slide"
      maskTransitionName="filters-fade"
      title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_TITLE)}
      classNames={{ body: 'no-scrollbar' }}
      styles={{
        content: { borderRadius: 20, border: '0.5px solid rgba(0,0,0,0.08)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', overflow: 'hidden' },
        body: { maxHeight: '65vh', overflowY: 'auto', padding: '0 28px', scrollbarWidth: 'none', msOverflowStyle: 'none' },
      }}
    >
      {renderRateSlider()}
      {renderCategoryGrid()}

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: token.colorBgContainer,
          margin: '16px -28px 0',
          padding: '16px 28px',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {renderClearFiltersButton()}
        {renderShowResultsButton()}
      </div>
    </Modal>
  );
};

export default OperatorFiltersModal;
